<template>
  <div>
    <div class="d-flex my-6">
      <SearchTextField
        v-model="searchQuery"
        :tooltipText="t('searchBarPlaceholder')"
      />
      <v-spacer />
      <v-skeleton-loader
        v-if="isRefreshing"
        type="image"
        class="download-button-skeleton"
      />
      <app-button
        v-if="isDownloadButtonDisplayed"
        data-locator="download-all-attachments-button"
        type="outlined"
        variant="neutral"
        :loading="isRefreshing"
        @click="downloadAllDisplayedFiles"
      >
        <app-icon-ds
          icon="arrow-down-to-line"
          class="text-grey-600"
          size="sm"
        />
        {{ t('downloadAllBtnLabel') }}
      </app-button>
    </div>

    <div v-if="selectedAttachedFiles.length > 0" class="mb-5">
      <ChecklistPaneFilesActionBar :mnemo="mnemo"/>
    </div>

    <v-data-table
      :items="displayedFiles"
      return-object
      :headers="headers"
      hide-default-footer
      show-select
      density="compact"
      class="ChecklistPaneFilesTreeTable mb-4"
      data-locator="files-table"
      :itemsPerPage="displayedFiles?.length"
      :loading="isRefreshing"
      :row-props="rowProps"
    >
      <template v-slot:header.data-table-select>
        <v-checkbox
          :model-value="folderCheckboxSelected"
          hide-details
          readonly
          class="mt-0 pt-0 mr-4"
          @click.stop="onGlobalCheckboxClick"
        />
      </template>

      <template v-slot:item.data-table-select="{ item }">
        <v-checkbox
          :model-value="selectedAttachedFiles.some(selectedFile => selectedFile.id === item.id)"
          hide-details
          readonly
          class="mt-0 pt-0"
          data-locator="select-checkbox"
          @click.stop="onFileCheckboxClick(item)"
        />
      </template>

      <template #loading>
        <v-skeleton-loader type="image" class="loading-row" />
      </template>

      <template #no-data>
        <div class="text-left text-grey-500">{{ t('table.noData') }}</div>
      </template>

      <template #header.updatedBy>
        <TruncatedText>{{ t('table.headers.updatedBy') }}</TruncatedText>
      </template>

      <template #item.basename="{ item }">
        <ChecklistPaneFilesTreeDocumentName :document="item" />
      </template>

      <template #item.updatedBy="{ item }">
        <AppTooltip>
          <template #activator="{ props }">
            <app-avatar 
              class="bg-green-50 no-shrink text-grey" 
              :initials="getInitials(item.updateUser)"
              v-bind="props"
            />
          </template>
          {{ item.updateUser }}
        </AppTooltip>
      </template>

      <template #item.updateTime="{ item }">
        <span class="text--small">{{ getFormatedDate(item.updateTime) }}</span>
      </template>

      <template #item.rights="{ item }">
        <RightsDisplayButton
          compact
          :rights="item.rights"
          @click="prepareSharePane(item)"
        />
      </template>
      <template #item.actions="{ item }">
        <ChecklistTableTaskAttachedFilesItemMenu :mnemo="mnemo" :file="item"/>
      </template>
    </v-data-table>
  </div>
</template>


<script>
import { cloneDeep , debounce } from 'lodash-es'
import { mapActions, mapMutations, mapGetters, mapState } from 'vuex'

import AppTooltip from '@/common/AppTooltip.vue'
import SearchTextField from '@/common/SearchTextField.vue'
import TruncatedText from '@/common/TruncatedText.vue'
import RightsDisplayButton from '@/common/users/RightsDisplayButton.vue'
import { ISOToShortenedDate } from '@/common/utils/dates'
import { useTranslation } from '@/common/utils/translation'
import ChecklistPaneFilesActionBar from '@/project/checklist/panes/ChecklistPaneFilesActionBar.vue'
import ChecklistTableTaskAttachedFilesItemMenu from '@/project/checklist/table/ChecklistTableTaskAttachedFilesItemMenu.vue'
import {
  ADD_SELECTED_ATTACHED_FILE,
  REMOVE_SELECTED_ATTACHED_FILE,
  SET_SELECTED_ATTACHED_FILE,
} from '@/store/modules/checklist/mutation_types'
import { DocumentSharePickerActionTypes } from '@/store/modules/document-share-picker/action_types'
import { OPEN_DOCUMENTS_DOWNLOAD_FILES } from '@/store/modules/documents-download-files/action_types'

import ChecklistPaneFilesTreeDocumentName from './ChecklistPaneFilesTreeDocumentName.vue'
import { SET_TABLE_DOCUMENTS_PANE } from '@/store/modules/documents/action_types'

export default {
  name: 'ChecklistPaneFilesTree',
  components: {
    AppTooltip,
    ChecklistPaneFilesTreeDocumentName,
    ChecklistPaneFilesActionBar,
    ChecklistTableTaskAttachedFilesItemMenu,
    RightsDisplayButton,
    SearchTextField,
    TruncatedText,
  },
  props: {
    mnemo: {
      type: String,
      required: true,
    },
    folderId: {
      type: Number,
      required: true,
    },
  },
  setup () {
    return useTranslation({ keyPrefix: 'project.checklist.panes.ChecklistPaneFilesTree' })
  },
  data () {
    return {
      searchQuery: '',
    }
  },
  computed: {
    ...mapState('checklist', ['taskFolderPendingIds', 'currentTaskFolder', 'selectedAttachedFiles']),
    ...mapGetters('room', ['isCurrentUserPm', 'isWopiEnabled']),
    search: {
      get () {
        return this.searchQuery
      },
      set: debounce(function (val) {
        this.searchQuery = val
      }, 1000),
    },
    defaultFiles () {
      return this.currentTaskFolder?.children || []
    },
    displayedFiles () {
      return this.search ? this.filteredFiles : this.defaultFiles
    },
    filteredFiles () {
      if (!this.search) return []
      return this.defaultFiles.filter((file) => {
        const searchQuery = this.search.toLowerCase()
        return file.basename.toLowerCase().includes(searchQuery) ||
          file.updateUser.toLowerCase().includes(searchQuery)
      })
    },
    isRefreshing () {
      return this.taskFolderPendingIds.includes(this.folderId)
    },
    folderCheckboxSelected () {
      return this.displayedFiles?.length > 0 && this.selectedAttachedFiles.length === this.displayedFiles?.length
    },
    isDownloadButtonDisplayed () {
      return !this.isRefreshing && this.displayedFiles.length > 0
    },
    headers() {
      const headersItems = [
        {
          value: 'basename',
          title: this.t('table.headers.filename'),
          sortable: true,
        },
        {
          value: 'updatedBy',
          title: this.t('table.headers.updatedBy'),
          sort (a, b) {
            return a?.updateUser?.localeCompare(b?.updateUser)
          },
        },
        {
          value: 'updateTime',
          title: this.t('table.headers.updateTime'),
        },
      ]

      if (this.isCurrentUserPm) {
        headersItems.push({
          value: 'rights',
          title: this.t('table.headers.rights'),
        })
      }

      headersItems.push({
        value: 'actions',
        title: '',
      })

      return headersItems
    },
  },
  methods: {
    ...mapActions('documentSharePicker', [
      DocumentSharePickerActionTypes.INIT_SELECTED_GROUPS,
      DocumentSharePickerActionTypes.INIT_SELECTED_MEMBERS,
    ]),
    ...mapActions('documentsDownloadFiles', [OPEN_DOCUMENTS_DOWNLOAD_FILES]),
    ...mapActions('documents', [SET_TABLE_DOCUMENTS_PANE]),
    ...mapMutations('checklist', [ADD_SELECTED_ATTACHED_FILE, REMOVE_SELECTED_ATTACHED_FILE, SET_SELECTED_ATTACHED_FILE]),
    prepareSharePane (item) {
      this.INIT_SELECTED_GROUPS(cloneDeep(item.rights.read.groups))
      this.INIT_SELECTED_MEMBERS(cloneDeep(item.rights.read.users))
      this.SET_TABLE_DOCUMENTS_PANE({
        isOpen: true,
        tab: 'share',
        documents: [item],
        checklistFilesParent: this.currentTaskFolder,
      })
    },
    onGlobalCheckboxClick () {
      if (this.folderCheckboxSelected) {
        this.displayedFiles.forEach(item => {
          this.REMOVE_SELECTED_ATTACHED_FILE(item)
        })
      } else {
        this.displayedFiles.forEach(item => {
          this.ADD_SELECTED_ATTACHED_FILE(item)
        })
      }
    },
    onFileCheckboxClick (item) {
      this.SET_SELECTED_ATTACHED_FILE(item)
    },
    downloadAllDisplayedFiles () {
      this.OPEN_DOCUMENTS_DOWNLOAD_FILES(this.displayedFiles)
    },
    getInitials (updateUser) {
      const initials = updateUser?.split(' ').map(word => word.charAt(0)).join('')
      return initials
    },
    getFormatedDate(dateToFormat) {
      return ISOToShortenedDate(dateToFormat)
    },
    rowProps(row) {
      const item = row.item
      return {
        'data-locator': 'documents-file-row',
        class: this.selectedAttachedFiles.some(selectedFile => selectedFile.id === item.id) ? 'selected' : '',
      }
    },
  },
}
</script>

<style scoped lang="scss">
.ChecklistPaneFilesTreeTable {
  border-radius: 12px;
  border: 1px solid rgb(var(--v-theme-grey-100));
  width: 100%;
  overflow: hidden;

  :deep(.v-data-table-progress) {
    display: none;
  }
}
.ChecklistPaneFilesTreeTable .v-table__wrapper {
  overflow: hidden;
}
:deep(.ChecklistPaneFilesTreeTable thead) {
  background: rgb(var(--v-theme-grey-25));

  th:not(:first-child) {
    color:rgb(var(--v-theme-grey-700)) !important;
    font-size: 14px;
    font-weight: 400 !important;
    padding: 0 6px;
  }

  th:first-child {
    max-width: 30px;
  }

  th:nth-child(3) {
    max-width: 55px;
  }
}

:deep(.ChecklistPaneFilesTreeTable tr) {
  white-space: nowrap;
  
  td:first-child {
    max-width: 20px !important;
  }

  td:not(:first-child) {
    padding: 0 6px;
  }
}
:deep(.ChecklistPaneFilesTreeTable tbody tr) {
  height: 44px;
}

.loading-row {
  :deep(.v-skeleton-loader__bone) {
    height: 24px;
  }
}

.download-button-skeleton {
  :deep(.v-skeleton-loader__bone) {
    width: 160px;
    height: 40px;
  }
}
.ChecklistPaneFilesActionBar {
  border: 2px solid rgb(var(--v-theme-darkblue-300)) !important;
}
:deep(.ChecklistPaneFilesTreeTable .v-table--density-comfortable) {
  --v-table-header-height: 40px !important;
}
:deep(.v-selection-control__wrapper) {
  width: auto;
}
:deep(.v-data-table__tr) {
  cursor: default;
  
  &:hover {
    background-color: rgb(var(--v-theme-grey-50));
  }
  &.selected {
     background-color: rgb(var(--v-theme-darkblue-50)) !important;
  }
}
</style>
